import React from 'react';
import { motion } from 'framer-motion';
import Navigation from './Navigation';
import Footer from './Footer';
import { Lock, Eye, Shield, Database } from 'lucide-react';

const PrivacyPage = () => {
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-center"
          >
            <div className="w-20 h-20 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-8">
              <Lock className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-emerald-400 bg-clip-text text-transparent">
              Privacy Policy
            </h1>
            <p className="text-lg text-white/80">
              Last updated: January 2024
            </p>
          </motion.div>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div className="bg-gradient-to-br from-slate-800/50 to-slate-700/30 backdrop-blur-lg border border-white/10 rounded-3xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                <Shield className="w-6 h-6 mr-3 text-emerald-400" />
                Your Privacy Matters
              </h2>
              <p className="text-white/80 leading-relaxed">
                At Agami Cabs, we are committed to protecting your privacy and ensuring the security of your personal information. 
                This Privacy Policy explains how we collect, use, and safeguard your data.
              </p>
            </div>

            <div className="space-y-8">
              <div className="bg-slate-800/30 rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                  <Database className="w-5 h-5 mr-3 text-emerald-400" />
                  Information We Collect
                </h3>
                <div className="text-white/80 space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-2">Personal Information:</h4>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Name and contact details (phone number, email address)</li>
                      <li>Pickup and destination locations</li>
                      <li>Travel preferences and special requirements</li>
                      <li>Payment information (processed securely through third-party providers)</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-2">Automatically Collected Information:</h4>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Device information and IP address</li>
                      <li>Website usage data and analytics</li>
                      <li>Location data (only when using our services)</li>
                      <li>Trip history and service preferences</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/30 rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">How We Use Your Information</h3>
                <div className="text-white/80 space-y-3">
                  <p>We use your personal information to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Provide and improve our transportation services</li>
                    <li>Process bookings and manage your account</li>
                    <li>Communicate about your trips and service updates</li>
                    <li>Ensure safety and security of our services</li>
                    <li>Analyze usage patterns to enhance user experience</li>
                    <li>Comply with legal requirements and resolve disputes</li>
                  </ul>
                </div>
              </div>

              <div className="bg-slate-800/30 rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">Information Sharing</h3>
                <div className="text-white/80 space-y-3">
                  <p><strong>We do not sell your personal information.</strong> We may share your information only in these limited circumstances:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>With Chauffeurs:</strong> Trip details necessary to provide the service</li>
                    <li><strong>Service Providers:</strong> Trusted partners who help operate our services (payment processors, analytics)</li>
                    <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                    <li><strong>Business Transfers:</strong> In case of merger, acquisition, or sale of assets</li>
                  </ul>
                </div>
              </div>

              <div className="bg-slate-800/30 rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">Data Security</h3>
                <div className="text-white/80 space-y-3">
                  <p>We implement robust security measures to protect your information:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>SSL encryption for all data transmission</li>
                    <li>Secure servers with restricted access</li>
                    <li>Regular security audits and updates</li>
                    <li>Employee training on data protection</li>
                    <li>Compliance with industry security standards</li>
                  </ul>
                </div>
              </div>

              <div className="bg-slate-800/30 rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">Your Rights and Choices</h3>
                <div className="text-white/80 space-y-3">
                  <p>You have the right to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Access:</strong> Request a copy of your personal information</li>
                    <li><strong>Correct:</strong> Update or correct inaccurate information</li>
                    <li><strong>Delete:</strong> Request deletion of your personal data</li>
                    <li><strong>Restrict:</strong> Limit how we use your information</li>
                    <li><strong>Portability:</strong> Receive your data in a portable format</li>
                    <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                  </ul>
                </div>
              </div>

              <div className="bg-slate-800/30 rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">Location Information</h3>
                <div className="text-white/80 space-y-3">
                  <p><strong>Location Tracking:</strong> We collect location data only:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>During active trips to ensure efficient routing</li>
                    <li>When you voluntarily share your location for pickup</li>
                    <li>To provide location-based services and features</li>
                  </ul>
                  <p>You can disable location services in your device settings, though this may affect service quality.</p>
                </div>
              </div>

              <div className="bg-slate-800/30 rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">Cookies and Tracking</h3>
                <div className="text-white/80 space-y-3">
                  <p>We use cookies and similar technologies to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Remember your preferences and settings</li>
                    <li>Analyze website traffic and user behavior</li>
                    <li>Improve our website functionality</li>
                    <li>Provide personalized experiences</li>
                  </ul>
                  <p>You can control cookie settings through your browser preferences.</p>
                </div>
              </div>

              <div className="bg-slate-800/30 rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">Data Retention</h3>
                <div className="text-white/80 space-y-3">
                  <p>We retain your information for as long as necessary to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Provide our services and maintain your account</li>
                    <li>Comply with legal obligations</li>
                    <li>Resolve disputes and enforce agreements</li>
                    <li>Improve our services and safety measures</li>
                  </ul>
                  <p>When no longer needed, data is securely deleted or anonymized.</p>
                </div>
              </div>

              <div className="bg-slate-800/30 rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">Contact Us</h3>
                <div className="text-white/80 space-y-3">
                  <p>For privacy-related questions or to exercise your rights, contact us:</p>
                  <ul className="space-y-2">
                    <li><strong>Email:</strong> hello@agamicabs.com</li>
                    <li><strong>Phone:</strong> +91 99999 99999</li>
                    <li><strong>Subject Line:</strong> "Privacy Policy Inquiry"</li>
                  </ul>
                </div>
              </div>

              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-6">
                <div className="flex items-center mb-4">
                  <Eye className="w-6 h-6 text-emerald-400 mr-3" />
                  <h3 className="text-xl font-bold text-white">Policy Updates</h3>
                </div>
                <p className="text-white/80">
                  We may update this Privacy Policy from time to time. We will notify you of any 
                  significant changes by email or through our website. Your continued use of our 
                  services after such modifications constitutes acceptance of the updated Privacy Policy.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPage;